import { useActionSheet } from '@expo/react-native-action-sheet';
import * as Clipboard from 'expo-clipboard';
import { useNavigation } from '@react-navigation/native';
import { userInfoAtom } from 'app/atoms';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import ImageViewerModal from 'app/design/primitives/image-viewer-modal';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { RequestImage } from 'app/components/request-image';

import { localeAwareFormatDistance } from 'app/lib/date';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useLikePost, useUnlikePost } from 'app/network/post';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import type { PostResponse } from 'app/types/post';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { ListingType } from 'app/types/listing';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Share from 'react-native-share';
import Toast2 from 'react-native-root-toast';
import {
  FlatList,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  type StyleProp,
  StyleSheet,
  type TextLayoutEvent,
  View,
  type ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useAtomValue } from 'jotai';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCircleFilled, IconDots } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import Reanimated, {
  cancelAnimation,
  type ScrollHandlerProcessed,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
const theme = createDSTheme();
const POST_CAPTION_MAX_LINES = 3;

function ItemSeparator() {
  return <View style={{ height: theme.spacing['spacing.general.x5'] }} />;
}

interface PostCardProps {
  post: PostResponse;
  showListingAvatar: boolean;
}

function HeartOutlineIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="m20.84 4.61-1.54-1.35A5.5 5.5 0 0 0 12 4.09a5.5 5.5 0 0 0-7.3-.83L3.16 4.61a5.5 5.5 0 0 0-.41 7.78l7.73 7.11a2 2 0 0 0 2.72 0l7.73-7.11a5.5 5.5 0 0 0-.09-7.78Z"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </Svg>
  );
}

function HeartFilledIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="m20.84 4.61-1.54-1.35A5.5 5.5 0 0 0 12 4.09a5.5 5.5 0 0 0-7.3-.83L3.16 4.61a5.5 5.5 0 0 0-.41 7.78l7.73 7.11a2 2 0 0 0 2.72 0l7.73-7.11a5.5 5.5 0 0 0-.09-7.78Z"
        fill={color}
      />
    </Svg>
  );
}

function CommentOutlineIcon({ color, size = 20 }: { color: string; size?: number }) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5A8.48 8.48 0 0 1 21 11v.5Z"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </Svg>
  );
}

function MediaSlide({
  picture,
  listingName,
  listingAvatar,
}: {
  picture: PostResponse['pictures'][number];
  listingName: string;
  listingAvatar: React.ReactNode | null;
}) {
  return (
    <View style={styles.mediaFrame}>
      {listingAvatar}
      {picture.url ? (
        <PCImage contentFit="cover" source={{ uri: picture.url }} style={styles.mediaImage} transition={180} />
      ) : (
        <View style={styles.mediaFallbackBackground}>
          <Text color="surface.default" fontWeight="700" style={styles.mediaFallbackTitle}>
            {listingName}
          </Text>
        </View>
      )}
    </View>
  );
}

function getPostCardDetails(post: PostResponse, deletedUserLabel: string) {
  const listing = post.loan.listing;
  const lender = post.loan.lender;

  return {
    authorName: post.user.profile?.firstName ?? '-',
    commentsCount: post.commentsCount,
    imageUrls: post.pictures.map((picture) => ({ url: picture.url })),
    lender,
    lenderName: lender?.profile?.firstName ?? deletedUserLabel,
    listingId: listing.id,
    listingName: listing.item.name,
    listingPicture: listing.picture ?? '',
    listingType: listing.type,
    pictures: post.pictures,
    shareMessage: [post.user.profile?.firstName ?? '-', post.text, post.pictures[0]?.url].filter(Boolean).join('\n\n'),
    usesRequestIcon: listing.type === ListingType.request || !listing.picture,
  };
}

function usePostLike(post: PostResponse) {
  const likeScale = useSharedValue(1);
  const likeScaleStyle = useAnimatedStyle(() => ({ transform: [{ scale: likeScale.get() }] }));
  const [optimisticLike, setOptimisticLike] = useState({ source: post.isLiked, value: post.isLiked });
  let currentOptimisticLike = optimisticLike;

  if (optimisticLike.source !== post.isLiked) {
    currentOptimisticLike = { source: post.isLiked, value: post.isLiked };
    setOptimisticLike(currentOptimisticLike);
  }

  const likePost = useLikePost();
  const unlikePost = useUnlikePost();
  const isLiked = currentOptimisticLike.value;
  const isUpdatingLike = likePost.isPending || unlikePost.isPending;
  const likesCount = post.likesCount + Number(isLiked) - Number(post.isLiked);

  const toggleLike = () => {
    if (isUpdatingLike) return;
    const next = !isLiked;

    setOptimisticLike({ source: post.isLiked, value: next });

    if (next) {
      cancelAnimation(likeScale);
      likeScale.set(0.92);
      likeScale.set(
        withSequence(
          withTiming(1.14, { duration: 120 }),
          withSpring(1, { damping: 30.48, mass: 1, stiffness: 469.55 }),
        ),
      );
    }

    const mutation = next ? likePost : unlikePost;
    mutation.mutate(post.id, {
      onError: () => setOptimisticLike({ source: post.isLiked, value: post.isLiked }),
    });
  };

  return { isLiked, isUpdatingLike, likeScaleStyle, likesCount, toggleLike };
}

function PostCardHeader({
  authorName,
  hasPictures,
  navigation,
  onOpenActionsMenu,
  post,
  timestamp,
}: {
  authorName: string;
  hasPictures: boolean;
  navigation: SpecificNavigationProp;
  onOpenActionsMenu: () => void;
  post: PostResponse;
  timestamp: string;
}) {
  return (
    <Row
      alignItems="center"
      style={[styles.header, { paddingBottom: hasPictures ? theme.spacing['spacing.general.x3'] : 0 }]}
    >
      <Pressable
        onPress={() => navigateToUserProfile(navigation, post.user.id)}
        style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: theme.spacing['spacing.general.x2'] }}
      >
        <Thumbnail height={40} source={{ uri: post.user.profile?.picture ?? undefined }} />
        <Row alignItems="center" flex={1} flexWrap="wrap" gap="spacing.general.x2">
          <Text fontWeight="700" style={styles.authorName}>
            {authorName}
          </Text>
          <IconCircleFilled color={theme.colors['text.secondary']} width={4} height={4} />
          <Text style={styles.timestamp}>{timestamp}</Text>
        </Row>
      </Pressable>

      <Row alignItems="center" gap="spacing.general.x1">
        <Pressable hitSlop={10} onPress={onOpenActionsMenu} style={styles.menuButton}>
          <IconDots strokeWidth={ICON_STROKE_WIDTH} size={25} color={theme.colors['text.secondary']} />
        </Pressable>
      </Row>
    </Row>
  );
}

function PostListingAvatar({
  listingId,
  listingPicture,
  navigation,
  show,
  usesRequestIcon,
}: {
  listingId: string;
  listingPicture: string;
  navigation: SpecificNavigationProp;
  show: boolean;
  usesRequestIcon: boolean;
}) {
  if (!show) return null;

  return (
    <Pressable onPress={() => navigateToListing(navigation, listingId)} style={styles.listingAvatarButton}>
      {usesRequestIcon ? (
        <RequestImage
          fontSize={32}
          isFound
          style={{ height: '100%', width: '100%', borderRadius: theme.borderRadii['radius.full'] }}
        />
      ) : (
        <PCImage source={{ uri: listingPicture }} style={styles.listingAvatar} />
      )}
    </Pressable>
  );
}

function PostListingContext({
  lender,
  lenderLabel,
  lenderName,
  listingId,
  listingLabel,
  listingName,
  listingPicture,
  listingType,
  navigation,
  pictureCount,
  showListingAvatar,
  usesRequestIcon,
}: {
  lender: PostResponse['loan']['lender'];
  lenderLabel: string;
  lenderName: string;
  listingId: string;
  listingLabel: string;
  listingName: string;
  listingPicture: string;
  listingType: ListingType;
  navigation: SpecificNavigationProp;
  pictureCount: number;
  showListingAvatar: boolean;
  usesRequestIcon: boolean;
}) {
  if (!showListingAvatar || pictureCount !== 0) return null;

  return (
    <Row alignItems="center" gap="spacing.general.x3" style={styles.listingContext}>
      <Pressable onPress={() => navigateToListing(navigation, listingId)} style={styles.listingContextItem}>
        <View style={styles.listingContextThumbnail}>
          {usesRequestIcon ? (
            <RequestImage fontSize={20} isFound style={{ height: '100%', width: '100%' }} />
          ) : (
            <PCImage source={{ uri: listingPicture }} style={styles.listingAvatar} />
          )}
        </View>
        <Column flex={1} gap="spacing.general.x0,5">
          <Text color="text.secondary" fontSize={11} numberOfLines={1}>
            {listingLabel}
          </Text>
          <Text fontWeight="600" numberOfLines={1} style={styles.listingContextText}>
            {listingName}
          </Text>
        </Column>
      </Pressable>
      {lender ? (
        <>
          <View style={styles.listingContextDivider} />
          <Pressable
            onPress={() => navigateToUserProfile(navigation, lender.id, listingId, listingType)}
            style={styles.listingContextItem}
          >
            <Thumbnail height={36} source={{ uri: lender.profile?.picture ?? undefined }} />
            <Column flex={1} gap="spacing.general.x0,5">
              <Text color="text.secondary" fontSize={11} numberOfLines={1}>
                {lenderLabel}
              </Text>
              <Text fontWeight="600" numberOfLines={1} style={styles.listingContextText}>
                {lenderName}
              </Text>
            </Column>
          </Pressable>
        </>
      ) : null}
    </Row>
  );
}

function PostMedia({
  currentMediaWidth,
  isLargeUI,
  listingAvatar,
  listingName,
  onImagePress,
  onMediaLayout,
  onMediaScrollEnd,
  pictures,
  visibleImageIndex,
}: {
  currentMediaWidth: number;
  isLargeUI: boolean;
  listingAvatar: React.ReactNode;
  listingName: string;
  onImagePress: (index: number) => void;
  onMediaLayout: (event: { nativeEvent: { layout: { width: number } } }) => void;
  onMediaScrollEnd: (event: { nativeEvent: { contentOffset: { x: number } } }) => void;
  pictures: PostResponse['pictures'];
  visibleImageIndex: number;
}) {
  if (pictures.length === 0) return null;

  const singlePicture = pictures.length === 1 ? pictures[0] : undefined;
  const mediaContent = singlePicture ? (
    <Pressable onPress={() => onImagePress(0)}>
      <MediaSlide listingAvatar={listingAvatar} listingName={listingName} picture={singlePicture} />
    </Pressable>
  ) : (
    <FlatList
      data={pictures}
      decelerationRate="fast"
      horizontal
      keyExtractor={(item) => item.id}
      onMomentumScrollEnd={onMediaScrollEnd}
      pagingEnabled
      renderItem={({ item, index }) => (
        <Pressable onPress={() => onImagePress(index)} style={{ width: currentMediaWidth }}>
          <MediaSlide listingAvatar={listingAvatar} listingName={listingName} picture={item} />
        </Pressable>
      )}
      showsHorizontalScrollIndicator={false}
    />
  );

  return (
    <View onLayout={onMediaLayout} style={[styles.mediaSection, isLargeUI && styles.mediaSectionLarge]}>
      {mediaContent}
      {pictures.length > 1 ? (
        <View pointerEvents="none" style={styles.pagination}>
          {pictures.map((picture, index) => (
            <View
              key={picture.id}
              style={[styles.paginationDot, index === visibleImageIndex ? styles.paginationDotActive : null]}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}

function PostCaption({
  expanded,
  isCaptionTruncated,
  onTextLayout,
  onToggle,
  readLessLabel,
  readMoreLabel,
  text,
}: {
  expanded: boolean;
  isCaptionTruncated: boolean;
  onTextLayout: (event: TextLayoutEvent) => void;
  onToggle: () => void;
  readLessLabel: string;
  readMoreLabel: string;
  text: string;
}) {
  return (
    <Column gap="spacing.general.x1" style={styles.captionBlock}>
      <Text accessible={false} onTextLayout={onTextLayout} style={[styles.captionText, styles.captionMeasurementText]}>
        {text}
      </Text>
      <Text numberOfLines={expanded ? undefined : POST_CAPTION_MAX_LINES} style={styles.captionText}>
        {text}
      </Text>
      {isCaptionTruncated ? (
        <Pressable onPress={onToggle}>
          <Text color="text.interactive" fontWeight="600" style={styles.readMoreText}>
            {expanded ? readLessLabel : readMoreLabel}
          </Text>
        </Pressable>
      ) : null}
    </Column>
  );
}

function PostActions({
  commentsCount,
  isLiked,
  isUpdatingLike,
  likeScaleStyle,
  likesCount,
  onOpenComments,
  onToggleLike,
}: {
  commentsCount: number;
  isLiked: boolean;
  isUpdatingLike: boolean;
  likeScaleStyle: ReturnType<typeof useAnimatedStyle>;
  likesCount: number;
  onOpenComments: () => void;
  onToggleLike: () => void;
}) {
  return (
    <Row alignItems="center" gap="spacing.general.x5" style={styles.actionBar}>
      <Pressable disabled={isUpdatingLike} hitSlop={8} onPress={onToggleLike} style={styles.actionButton}>
        <Reanimated.View style={likeScaleStyle}>
          {isLiked ? (
            <HeartFilledIcon color={theme.colors['icon.error']} size={22} />
          ) : (
            <HeartOutlineIcon color={theme.colors['text.primary']} size={22} />
          )}
        </Reanimated.View>
        <Text style={styles.actionCount}>{likesCount}</Text>
      </Pressable>

      <Pressable hitSlop={8} onPress={onOpenComments} style={styles.actionButton}>
        <CommentOutlineIcon color={theme.colors['text.primary']} size={22} />
        <Text style={styles.actionCount}>{commentsCount}</Text>
      </Pressable>
    </Row>
  );
}

function PostCard({ post, showListingAvatar }: PostCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isCaptionTruncated, setIsCaptionTruncated] = useState(false);
  const { t } = useTranslation('posts');
  const { showActionSheetWithOptions } = useActionSheet();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImageIndex, setViewerImageIndex] = useState(0);
  const [visibleImageIndex, setVisibleImageIndex] = useState(0);
  const [mediaWidth, setMediaWidth] = useState(0);
  const navigation = useNavigation<SpecificNavigationProp>();
  const currentUser = useAtomValue(userInfoAtom);
  const isOwner = currentUser?.id === post.userId;
  const { isLargeUI } = useUISize();
  const { isLiked, isUpdatingLike, likeScaleStyle, likesCount, toggleLike } = usePostLike(post);

  const handleCaptionTextLayout = (event: TextLayoutEvent) => {
    setIsCaptionTruncated((wasTruncated) => {
      const isTruncated = event.nativeEvent.lines.length > POST_CAPTION_MAX_LINES;
      return wasTruncated === isTruncated ? wasTruncated : isTruncated;
    });
  };

  const details = getPostCardDetails(post, t('deletedUser', { ns: 'common' }));

  const handleImagePress = (index: number) => {
    setViewerImageIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleMediaLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    const nextWidth = Math.round(event.nativeEvent.layout.width);
    setMediaWidth((current) => (current === nextWidth ? current : nextWidth));
  };

  const handleMediaScrollEnd = (event: { nativeEvent: { contentOffset: { x: number } } }) => {
    if (!mediaWidth) return;
    const nextIndex = Math.round(event.nativeEvent.contentOffset.x / mediaWidth);
    setVisibleImageIndex(nextIndex);
  };

  const handleEdit = () => {
    navigation.navigate(
      'edit-post',
      {
        postId: post.id,
        listingId: post.loan.listing.id,
      },
      { pop: true },
    );
  };

  const handleOpenComments = () => {
    navigation.navigate('post-comments', { postId: post.id }, { pop: true });
  };

  const handleSharePost = async () => {
    if (isMobileNative) {
      await Share.open({
        title: `${details.authorName} · ${details.listingName}`,
        message: details.shareMessage,
        url: details.pictures[0]?.url ?? undefined,
      }).catch(() => ({ message: 'cancel' }));
      return;
    }

    await Clipboard.setStringAsync(details.shareMessage);
    Toast2.show(t('feed.shareCopied'), {
      duration: Toast2.durations.SHORT,
    });
  };

  const handleReportPost = () => {
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
      i18nKey: 'feed.reportPostPrompt',
      ns: 'posts',
      values: { item: details.listingName, name: details.authorName },
    });
  };

  const handleOpenActionsMenu = () => {
    const options = isOwner
      ? [t('feed.shareAction'), t('feed.editAction'), t('feed.cancel')]
      : [t('feed.shareAction'), t('feed.reportAction'), t('feed.cancel')];

    const cancelButtonIndex = options.length - 1;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
      },
      (selectedOption) => {
        if (selectedOption === cancelButtonIndex || selectedOption === undefined) return;

        if (selectedOption === 0) {
          void handleSharePost();
          return;
        }

        if (isOwner) {
          handleEdit();
          return;
        }

        handleReportPost();
      },
    );
  };

  const currentMediaWidth = mediaWidth || 320;
  const listingAvatar = (
    <PostListingAvatar
      listingId={details.listingId}
      listingPicture={details.listingPicture}
      navigation={navigation}
      show={showListingAvatar}
      usesRequestIcon={details.usesRequestIcon}
    />
  );

  return (
    <View style={styles.cardShadow}>
      <View style={styles.card}>
        <ImageViewerModal
          key={viewerImageIndex}
          imageUrls={details.imageUrls}
          index={viewerImageIndex}
          onClose={() => setIsImageViewerOpen(false)}
          open={isImageViewerOpen}
        />

        <PostCardHeader
          authorName={details.authorName}
          hasPictures={details.pictures.length > 0}
          navigation={navigation}
          onOpenActionsMenu={handleOpenActionsMenu}
          post={post}
          timestamp={t('feed.timeAgo', {
            time: localeAwareFormatDistance(new Date(), new Date(post.createdAt)),
          })}
        />
        <PostMedia
          currentMediaWidth={currentMediaWidth}
          isLargeUI={isLargeUI}
          listingAvatar={listingAvatar}
          listingName={details.listingName}
          onImagePress={handleImagePress}
          onMediaLayout={handleMediaLayout}
          onMediaScrollEnd={handleMediaScrollEnd}
          pictures={details.pictures}
          visibleImageIndex={visibleImageIndex}
        />
        <PostCaption
          expanded={expanded}
          isCaptionTruncated={isCaptionTruncated}
          onTextLayout={handleCaptionTextLayout}
          onToggle={() => setExpanded((current) => !current)}
          readLessLabel={t('feed.readLess')}
          readMoreLabel={t('feed.readMore')}
          text={post.text}
        />
        <PostListingContext
          lender={details.lender}
          lenderLabel={t('feed.lenderLabel')}
          lenderName={details.lenderName}
          listingId={details.listingId}
          listingLabel={t('feed.listingLabel')}
          listingName={details.listingName}
          listingPicture={details.listingPicture}
          listingType={details.listingType}
          navigation={navigation}
          pictureCount={details.pictures.length}
          showListingAvatar={showListingAvatar}
          usesRequestIcon={details.usesRequestIcon}
        />
        <PostActions
          commentsCount={details.commentsCount}
          isLiked={isLiked}
          isUpdatingLike={isUpdatingLike}
          likeScaleStyle={likeScaleStyle}
          likesCount={likesCount}
          onOpenComments={handleOpenComments}
          onToggleLike={toggleLike}
        />
        {/* {pictures.length === 0 && showListingAvatar && hasLoanParticipants ? (
          <Pressable onPress={() => navigateToListing(navigation, listingId)}>
            <Row alignItems="center" gap="spacing.general.x2" style={styles.listingContextCard}>
              <PCImage source={post.loan.lender?.profile?.picture} width={36} height={36} style={{borderRadius: theme.borderRadii['radius.lg'] }} />
              <Text fontWeight="600" numberOfLines={1} style={styles.listingContextText}>
                {t('feed.loanContext', { listingName, lenderName: post.loan.lender?.profile?.firstName, borrowerName: post.loan.borrower?.profile?.firstName })}
              </Text>
              
            </Row>
          </Pressable>
        ) : null} */}
      </View>
    </View>
  );
}

interface PostsListProps {
  posts: PostResponse[];
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  onEndReached?: () => void;
  /**
   * When true, displays a small avatar of the related listing in the post header.
   * Use on the user feed (where posts are detached from listing context).
   * Defaults to false so listing screens don't repeat their own thumbnail.
   */
  showListingAvatar?: boolean;
  onScroll?: ScrollHandlerProcessed | ((event: NativeSyntheticEvent<NativeScrollEvent>) => void) | undefined;
  contentContainerStyle?: StyleProp<ViewStyle>;
  ListHeaderComponent?: React.ComponentProps<typeof FlatList>['ListHeaderComponent'];
}

export function PostsList({
  posts,
  isLoading,
  isFetchingNextPage,
  onEndReached,
  showListingAvatar = false,
  onScroll,
  contentContainerStyle,
  ListHeaderComponent,
}: PostsListProps) {
  const { t } = useTranslation('posts');

  const renderPost = ({ item }: { item: PostResponse }) => (
    <PostCard post={item} showListingAvatar={showListingAvatar} />
  );

  if (isLoading && !ListHeaderComponent) {
    return (
      <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x12">
        <Text color="text.placeholder">{t('feed.loading')}</Text>
      </Column>
    );
  }

  return (
    <Reanimated.FlatList
      contentContainerStyle={[
        contentContainerStyle,
        !contentContainerStyle
          ? {
              paddingTop: theme.spacing['spacing.general.x2'],
              paddingBottom: theme.spacing['spacing.general.x36'],
            }
          : {},
      ]}
      data={posts}
      ListHeaderComponent={ListHeaderComponent}
      onScroll={onScroll}
      ItemSeparatorComponent={ItemSeparator}
      keyExtractor={(item) => item.id}
      ListEmptyComponent={
        isLoading ? (
          <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x12">
            <Text color="text.placeholder">{t('feed.loading')}</Text>
          </Column>
        ) : (
          <Column alignItems="center" gap="spacing.general.x2" padding="spacing.general.x12">
            <Text color="text.primary" fontWeight="700" textAlign="center" variant="bodyLarge">
              {t('feed.emptyTitle')}
            </Text>
            <Text color="text.secondary" textAlign="center" variant="bodyBase">
              {t('feed.emptyText')}
            </Text>
          </Column>
        )
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Column alignItems="center" padding="spacing.general.x4">
            <Text color="text.placeholder">{t('feed.loading')}</Text>
          </Column>
        ) : null
      }
      onEndReached={onEndReached}
      onEndReachedThreshold={0.4}
      renderItem={renderPost}
      showsVerticalScrollIndicator={false}
      style={styles.feedList}
    />
  );
}

const styles = StyleSheet.create({
  feedList: {
    backgroundColor: theme.colors['surface.default'],
  },
  cardShadow: {
    // borderRadius: theme.spacing['spacing.general.x4'],
    backgroundColor: theme.colors['icon.inverse.primary'],
    boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.1)',
  },
  card: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    // borderRadius: theme.spacing['spacing.general.x4'],
    overflow: 'hidden',
    borderColor: theme.colors['border.secondary'],
    borderWidth: 0.5,
  },
  header: {
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x4'],
  },
  authorName: {
    color: theme.colors['text.primary'],
    fontSize: 15,
    lineHeight: 20,
  },
  metaSep: {
    color: theme.colors['text.primary'],
    fontSize: 50,
  },
  timestamp: {
    color: theme.colors['text.secondary'],
    fontSize: 12,
    lineHeight: 20,
  },
  listingAvatar: {
    borderRadius: theme.borderRadii['radius.full'],
    height: '100%',
    width: '100%',
  },
  listingAvatarButton: {
    borderRadius: theme.borderRadii['radius.full'],
    position: 'absolute',
    boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.4)',
    top: theme.spacing['spacing.general.x5'],
    right: theme.spacing['spacing.general.x5'],
    zIndex: 1,
    height: 70,
    width: 70,
  },
  listingContext: {
    marginTop: theme.spacing['spacing.general.x3'],
    borderColor: theme.colors['border.secondary'],
    borderTopWidth: 1,
    marginHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x3'],
  },
  listingContextThumbnail: {
    borderRadius: theme.borderRadii['radius.md'],
    height: 36,
    overflow: 'hidden',
    width: 36,
  },
  listingContextItem: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x2'],
    minWidth: 0,
  },
  listingContextDivider: {
    backgroundColor: theme.colors['border.secondary'],
    height: 32,
    width: 1,
  },
  listingContextText: {
    color: theme.colors['text.primary'],
    flex: 1,
    fontSize: 13,
    lineHeight: 16,
  },
  menuButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  mediaSection: {
    marginTop: theme.spacing['spacing.general.x1'],
  },
  mediaSectionLarge: {
    aspectRatio: 16 / 9,
  },
  mediaFrame: {
    aspectRatio: 4 / 5,
    backgroundColor: '#F5F4F1',
    borderColor: 'rgba(0,0,0,0.06)',
    borderWidth: 1,
  },
  mediaImage: {
    height: '100%',
    width: '100%',
  },
  mediaFallbackBackground: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.default'],
    justifyContent: 'center',
    padding: theme.spacing['spacing.general.x4'],
  },
  mediaFallbackTitle: {
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'center',
  },
  pagination: {
    alignItems: 'center',
    alignSelf: 'center',
    bottom: theme.spacing['spacing.general.x4'],
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    paddingVertical: theme.spacing['spacing.general.x0,5'],
    paddingHorizontal: theme.spacing['spacing.general.x1'],
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x1'],
    justifyContent: 'center',
    position: 'absolute',
  },
  paginationDot: {
    backgroundColor: 'rgba(255,255,255,0.5)',
    borderRadius: 999,
    height: 6,
    width: 6,
  },
  paginationDotActive: {
    backgroundColor: theme.colors['icon.inverse.primary'],
    width: 18,
  },
  captionBlock: {
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x4'],
  },
  captionText: {
    color: theme.colors['text.primary'],
    fontSize: 15,
    lineHeight: 22,
  },
  captionMeasurementText: {
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
  },
  readMoreText: {
    fontSize: 14,
    lineHeight: 18,
  },
  actionBar: {
    paddingBottom: theme.spacing['spacing.general.x4'],
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x3'],
  },
  actionButton: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  actionCount: {
    color: theme.colors['text.primary'],
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 16,
  },
});
